import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_DAY_TRIP_PLAN_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductDayTripPlanFormModel } from "./type";
import { dayTripPlanInputZodSchema, type DayTripPlanInputZodSchemaType } from "./validation-schema";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../_shared/model/repository/mutation/reservation-update";

import type { ProductDayTripPlanFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductDayTripPlanFormModel["InputModel"];
	queryData: ProductDayTripPlanFormModel["QueryData"];
};

export const useInitProductDayTripPlanFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductDayTripPlanFormModelContextType, "queryData"> & { resetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const planID = _inputModel.planID;

	const defaultInputModel = DEFAULT_PRODUCT_DAY_TRIP_PLAN_INPUT_MODEL({ planID, hotelID, queryData });
	const initInput = _inputModel ?? defaultInputModel;
	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductDayTripPlanFormModel["InputModel"],
			ExcludeKeys,
			ProductDayTripPlanFormModel["QueryData"],
			DayTripPlanInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput,
			currentInput: initInput,
			excludeKeys: excludeKeys,
			zodSchema: dayTripPlanInputZodSchema,
		}),
	);

	const onSetViewModel: ProductDayTripPlanFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductDayTripPlanFormModel["ViewModel"],
				),
			} satisfies ProductDayTripPlanFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductDayTripPlanFormModel["InputModel"],
				ExcludeKeys,
				ProductDayTripPlanFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductDayTripPlanFormModel["InputModel"],
				ExcludeKeys,
				ProductDayTripPlanFormModel["QueryData"],
				DayTripPlanInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: dayTripPlanInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, initInput, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();

	return {
		viewModel,
		initInputModel: initInput,
		onSetViewModel,
		resetViewModel,

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
